import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface KnowledgeBase {
  id: string;
  creator_id: string;
  title: string;
  description?: string;
  content?: string;
  file_url?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  domain_id?: string;
  subdomain_id?: string;
  version: number;
  is_bounty: boolean;
  bounty_amount?: number;
  status: string;
  views_count: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  deleted_at?: string;
  nft_cover_image_url?: string;
  profiles?: {
    display_name: string;
    avatar_url?: string;
  };
  domains?: {
    name: string;
    symbol?: string;
  };
  subdomains?: {
    name: string;
    description?: string;
  };
  attributions?: Array<{
    user_id: string;
    ownership_percentage: number;
    contribution_type: string;
    profiles: {
      display_name: string;
      avatar_url?: string;
    };
  }>;
}

export const useKnowledgeBases = () => {
  return useQuery({
    queryKey: ['knowledge_bases'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_bases')
        .select(`
          *,
          profiles:creator_id(display_name, avatar_url),
          domains:domain_id(name, symbol),
          subdomains:subdomain_id(name, description)
        `)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as KnowledgeBase[];
    }
  });
};

export const useUserKnowledgeBases = (userId?: string) => {
  return useQuery({
    queryKey: ['knowledge_bases', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('knowledge_bases')
        .select(`
          *,
          domains:domain_id(name, symbol),
          subdomains:subdomain_id(name, description),
          attributions(
            user_id,
            ownership_percentage,
            contribution_type,
            profiles:user_id(display_name, avatar_url)
          )
        `)
        .eq('creator_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as KnowledgeBase[];
    },
    enabled: !!userId
  });
};

export const useAccessibleKnowledgeBases = (userId?: string) => {
  return useQuery({
    queryKey: ['accessible_knowledge_bases', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase.functions.invoke('search-knowledge-bases', {
        body: { userId, query: '' }
      });

      if (error) throw error;

      // Return only accessible KBs (from kb_access table)
      return data?.accessible?.map((item: any) => item.knowledge_bases) || [];
    },
    enabled: !!userId
  });
};

export const useUserFullAccessKnowledgeBases = (userId?: string) => {
  return useQuery({
    queryKey: ['full_access_knowledge_bases', userId],
    queryFn: async () => {
      if (!userId) return [];

      // Fetch KBs created by the user
      const { data: createdKbs, error: createdError } = await supabase
        .from('knowledge_bases')
        .select(`
          *,
          domains:domain_id(name, symbol),
          subdomains:subdomain_id(name, description),
          attributions(
            user_id,
            ownership_percentage,
            contribution_type,
            profiles:user_id(display_name, avatar_url)
          )
        `)
        .eq('creator_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (createdError) throw createdError;

      // Fetch KBs user is attributed to (team member)
      const { data: attributedData, error: attributedError } = await supabase
        .from('attributions')
        .select(`
          knowledge_base_id,
          knowledge_bases!inner(
            *,
            domains:domain_id(name, symbol),
            subdomains:subdomain_id(name, description),
            attributions(
              user_id,
              ownership_percentage,
              contribution_type,
              profiles:user_id(display_name, avatar_url)
            )
          )
        `)
        .eq('user_id', userId);

      if (attributedError) throw attributedError;

      // Fetch KBs user has explicit access to (commercial license)
      const { data: licensedData, error: licensedError } = await supabase
        .from('kb_access')
        .select(`
          knowledge_base_id,
          knowledge_bases!inner(
            *,
            domains:domain_id(name, symbol),
            subdomains:subdomain_id(name, description),
            attributions(
              user_id,
              ownership_percentage,
              contribution_type,
              profiles:user_id(display_name, avatar_url)
            )
          )
        `)
        .eq('user_id', userId);

      if (licensedError) throw licensedError;

      // Merge and deduplicate by KB id
      const kbMap = new Map<string, KnowledgeBase>();

      // Add created KBs
      createdKbs?.forEach(kb => {
        kbMap.set(kb.id, kb as KnowledgeBase);
      });

      // Add attributed KBs (extract from nested structure)
      attributedData?.forEach(item => {
        const kb = (item as any).knowledge_bases;
        if (kb && !kbMap.has(kb.id)) {
          kbMap.set(kb.id, kb as KnowledgeBase);
        }
      });

      // Add licensed KBs (extract from nested structure)
      licensedData?.forEach(item => {
        const kb = (item as any).knowledge_bases;
        if (kb && !kbMap.has(kb.id)) {
          kbMap.set(kb.id, kb as KnowledgeBase);
        }
      });

      // Convert to array and sort by created_at
      return Array.from(kbMap.values()).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    enabled: !!userId
  });
};
